import React from "react";
import { DeskPanel, DeskPill } from "../desktop/DeskTable";
import {
  formatInvoiceStatus,
  resolveInvoiceStatus,
} from "../../helpers/invoices";
import {
  formatDaysUntilDueLabel,
  formatInvoiceCollectionStatusLabel,
  getInvoiceCollectionStatus,
  shouldShowInvoiceDueInfo,
} from "../../helpers/invoiceDueStatus";
import { formatInvoiceDueDateDisplay } from "./InvoiceDueStatus";
import "../desktop/deskKit.css";
import "./invoiceDesktop.css";

const STATUS_TONE = { draft: "muted", sent: "gold", paid: "green", archived: "red" };
const COLLECTION_TONE = {
  paid: "green",
  current: "blue",
  due_soon: "gold",
  overdue: "red",
};

export function invoiceStatusTone(status) {
  return STATUS_TONE[resolveInvoiceStatus(status)] || "muted";
}

export function InvoiceStatusPill({ status, label }) {
  const tone =
    label && /overdue/i.test(label) ? "red" : invoiceStatusTone(status);
  return (
    <DeskPill tone={tone}>{label || formatInvoiceStatus(status)}</DeskPill>
  );
}

/** Due date + collection state (sent / paid only — same rule as the mobile cards). */
export function InvoiceDueCell({ invoice }) {
  if (!shouldShowInvoiceDueInfo(invoice)) {
    return <span className="dt-muted">—</span>;
  }
  const collection = getInvoiceCollectionStatus(invoice);
  const dueLabel = formatDaysUntilDueLabel(invoice);
  return (
    <span className="inv-due">
      <span className="inv-due__top">
        <span className="dt-num dt-nowrap">{formatInvoiceDueDateDisplay(invoice)}</span>
        {collection ? (
          <DeskPill tone={COLLECTION_TONE[collection] || "muted"}>
            {formatInvoiceCollectionStatusLabel(collection, invoice)}
          </DeskPill>
        ) : null}
      </span>
      {dueLabel && collection !== "paid" ? (
        <span className={`inv-due__sub${collection === "overdue" ? " is-overdue" : ""}`}>{dueLabel}</span>
      ) : null}
    </span>
  );
}

export function money(amount) {
  return `$${Number(amount || 0).toFixed(2)}`;
}

export function InvRow({ label, value, money: isMoney = false, total = false, onClick, sub }) {
  const body = (
    <>
      <span className="inv-row__text">
        <span className="inv-row__label">{label}</span>
        {sub ? <span className="inv-row__sub">{sub}</span> : null}
      </span>
      <span className={`inv-row__value${isMoney ? " inv-row__value--money" : ""}`}>{value}</span>
    </>
  );
  if (onClick) {
    return (
      <li>
        <button type="button" className="inv-row inv-row--link" onClick={onClick}>
          {body}
        </button>
      </li>
    );
  }
  return (
    <li>
      <div className={`inv-row${total ? " inv-row--total" : ""}`}>{body}</div>
    </li>
  );
}

/** Outstanding Receivables panel — same numbers as AccountsReceivableDashboard. */
export function ReceivablesAgingPanel({ metrics, loading }) {
  const aging = metrics?.aging || {};
  return (
    <DeskPanel title="Outstanding Receivables">
      {loading ? (
        <p className="inv-note">Loading receivables…</p>
      ) : (
        <ul className="inv-rows">
          <InvRow label="Total Open" value={money(metrics?.outstandingReceivables)} money total />
          <InvRow label="Current" value={money(aging.current)} money />
          <InvRow label="1–30" value={money(aging.days_30)} money />
          <InvRow label="31–60" value={money(aging.days_60)} money />
          <InvRow label="61–90" value={money(aging.days_90)} money />
          <InvRow label="90+" value={money(aging.days_90_plus)} money />
        </ul>
      )}
    </DeskPanel>
  );
}

/** Collections Snapshot panel — same numbers as AccountsReceivableDashboard. */
export function CollectionsPanel({ metrics, loading }) {
  return (
    <DeskPanel title="Collections Snapshot">
      {loading ? (
        <p className="inv-note">Loading receivables…</p>
      ) : (
        <ul className="inv-rows">
          <InvRow label="Paid This Month" value={money(metrics?.paidThisMonth)} money />
          <InvRow label="Open Invoices" value={metrics?.invoiceCount ?? 0} />
          <InvRow label="Customers With Balance" value={metrics?.customerCount ?? 0} />
        </ul>
      )}
    </DeskPanel>
  );
}
